import { useFrame } from "@react-three/fiber";
import { useRef } from "react";
import * as THREE from "three";

// Base CRT screen color - warm amber tint (cozy CRT glow)
const SCREEN_LIGHT_COLOR = new THREE.Color(1.0, 0.9, 0.75);

export interface ScreenEmissionLightProps {
  /** The render target containing screen content */
  renderTarget: THREE.WebGLRenderTarget;
  /** The Three.js WebGL renderer */
  renderer: THREE.WebGLRenderer;
  /** Width of the light area (default: 2, matching CRTEffect geometry) */
  width?: number;
  /** Height of the light area (default: 2, matching CRTEffect geometry) */
  height?: number;
  /** Z offset from the screen surface - positive = in front (default: 0.01) */
  zOffset?: number;
  /** Power state (0-1) for fading light with power on/off (default: 1) */
  powerState?: number;
}

/**
 * Dynamic screen emission light - constant color, brightness varies based on screen content.
 * Creates a realistic CRT glow effect that illuminates the surrounding scene.
 *
 * Positioned relative to the CRTEffect mesh (sibling component), which uses a 2x2 plane.
 * The parent group in CRTFrame handles the actual world-space positioning and scaling.
 */
export function ScreenEmissionLight({
  renderTarget,
  renderer,
  width = 2.5,
  height = 2,
  zOffset = -0.01,
  powerState = 1,
}: ScreenEmissionLightProps) {
  const areaLightRef = useRef<THREE.RectAreaLight>(null);
  const sampleSize = 16; // Larger sample for more stable readings
  const pixelBuffer = useRef(new Uint8Array(4 * sampleSize * sampleSize));
  const frameCount = useRef(0);
  const smoothedBrightness = useRef(0.5); // Smoothed brightness value

  useFrame(() => {
    // Only sample every 5 frames for performance
    frameCount.current++;
    if (frameCount.current % 5 !== 0) return;
    if (!areaLightRef.current) return;

    try {
      const rtWidth = renderTarget.width;
      const rtHeight = renderTarget.height;

      // Sample from multiple regions across the screen for better average
      const startX = Math.floor((rtWidth - sampleSize) / 2);
      const startY = Math.floor((rtHeight - sampleSize) / 2);

      // Store current render target
      const currentTarget = renderer.getRenderTarget();

      // Read pixels from render target
      renderer.setRenderTarget(renderTarget);
      renderer.readRenderTargetPixels(
        renderTarget,
        startX,
        startY,
        sampleSize,
        sampleSize,
        pixelBuffer.current,
      );

      // Restore render target
      renderer.setRenderTarget(currentTarget);

      // Calculate overall brightness from sampled pixels
      let totalBrightness = 0;
      const pixelCount = sampleSize * sampleSize;
      const buffer = pixelBuffer.current;

      for (let i = 0; i < buffer.length; i += 4) {
        const r = (buffer[i] ?? 0) / 255;
        const g = (buffer[i + 1] ?? 0) / 255;
        const b = (buffer[i + 2] ?? 0) / 255;
        // Calculate luminance for each pixel
        totalBrightness += 0.299 * r + 0.587 * g + 0.114 * b;
      }

      const avgBrightness = totalBrightness / pixelCount;

      // Remap brightness from expected range (~0.32-0.45) to 0-1 for more dramatic variation
      const minExpected = 0.32;
      const maxExpected = 0.45;
      const normalizedBrightness = Math.max(
        0,
        Math.min(
          1,
          (avgBrightness - minExpected) / (maxExpected - minExpected),
        ),
      );

      // Smooth the brightness changes (lerp toward target)
      const smoothingFactor = 0.15;
      smoothedBrightness.current +=
        (normalizedBrightness - smoothedBrightness.current) * smoothingFactor;

      // Update intensity based on smoothed brightness and power state
      // Range: 0.3 (darker screen) to 1.5 (bright screen)
      const baseIntensity = 0.3 + smoothedBrightness.current * 1.2;
      areaLightRef.current.intensity = baseIntensity * powerState;
    } catch {
      // Fallback if reading fails
    }
  });

  return (
    // RectAreaLight positioned relative to CRTEffect mesh (same parent group)
    // Slightly in front of the screen surface to emit light outward
    <rectAreaLight
      ref={areaLightRef}
      position={[0, 0, zOffset]}
      rotation={[0, Math.PI, 0]} // Facing forward (+Z toward camera)
      width={width}
      height={height}
      intensity={0}
      color={SCREEN_LIGHT_COLOR}
    />
  );
}
